import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    GeographicalChartPointDto,
    GeographicalProjectLinkDto,
    GeographicalSummaryDto
} from './geographical-summary.api-contracts';
import { mapChartPoint, mapProjectLink, mapSummary } from './geographical-summary.mappers';
import {
    GeographicalChartPoint,
    GeographicalProjectLink,
    GeographicalSummaryResponse
} from './geographical-summary.types';

@Injectable()
export class GeographicalSummaryDataService {
    private readonly summaryEndpoint = '/ds5/geographicalsummary/v2/summary';
    private readonly chartCapexEndpoint = '/ds5/geographicalsummary/v2/chart/capex';
    private readonly chartCapacityEndpoint = '/ds5/geographicalsummary/v2/chart/capacity';
    private readonly projectsEndpoint = '/ds5/geographicalsummary/v2/projects';

    constructor(private http: HttpClient) { }

    getSummary(sectorId: number | null, regionId: number | null, countryId: number | null): Observable<GeographicalSummaryResponse> {
        const params = this.buildSummaryParams(sectorId, regionId, countryId);
        return this.http.get<GeographicalSummaryDto>(this.summaryEndpoint, { params }).pipe(
            map((dto) => mapSummary(dto))
        );
    }

    getChart(countryId: number, sectorId: number | null, metric: 'capex' | 'capacity' = 'capex'): Observable<GeographicalChartPoint[]> {
        const params = this.buildCountryParams(countryId, sectorId);
        const endpoint = metric === 'capacity' ? this.chartCapacityEndpoint : this.chartCapexEndpoint;
        return this.http.get<GeographicalChartPointDto[]>(endpoint, { params }).pipe(
            map((dto) => (dto || []).map((item) => mapChartPoint(item)))
        );
    }

    getProjects(countryId: number, sectorId: number | null): Observable<GeographicalProjectLink[]> {
        const params = this.buildCountryParams(countryId, sectorId);
        return this.http.get<GeographicalProjectLinkDto[]>(this.projectsEndpoint, { params }).pipe(
            map((dto) => (dto || []).map((item) => mapProjectLink(item)))
        );
    }

    private buildSummaryParams(sectorId: number | null, regionId: number | null, countryId: number | null): HttpParams {
        let params = new HttpParams();
        if (sectorId) {
            params = params.set('sectorId', String(sectorId));
        }

        if (regionId) {
            params = params.set('regionId', String(regionId));
        }

        if (countryId) {
            params = params.set('countryId', String(countryId));
        }

        return params;
    }

    private buildCountryParams(countryId: number, sectorId: number | null): HttpParams {
        let params = new HttpParams().set('countryId', String(countryId));
        if (sectorId) {
            params = params.set('sectorId', String(sectorId));
        }

        return params;
    }
}
